import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetApiMe } from '@/api/generated/user/user'
import { usePostApiMeEmailChange } from '@/api/generated/company-management-user/company-management-user'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { parseApiError, resolveApiErrorMessage } from '@/lib/apiError'
import { notify } from '@/lib/notify'
import { validateNewEmail } from '@/pages/profile/emailChange'

interface Props {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export function EmailChangeDialog({ isOpen, onOpenChange }: Props) {
  const { t } = useTranslation(['profile', 'common'])
  const { data: meData } = useGetApiMe()
  const me = meData?.data

  const [newEmail, setNewEmail] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { mutateAsync: requestEmailChange, isPending } = usePostApiMeEmailChange()

  const reset = () => {
    setNewEmail('')
    setError(null)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) reset()
    onOpenChange(open)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const validationError = validateNewEmail(newEmail, me?.email)
    if (validationError) {
      setError(t(`emailDialog.errors.${validationError}`))
      return
    }
    setError(null)
    const email = newEmail.trim()
    try {
      await requestEmailChange({ data: { newEmail: email } })
      notify.success(t('emailDialog.toasts.success', { email }), {
        description: t('emailDialog.toasts.successDescription'),
      })
      handleOpenChange(false)
    } catch (err) {
      // "Already taken" is shown at the field, everything else as toast.
      const parsed = parseApiError(err)
      if (parsed.errorCode === 'UserEmailAlreadyTaken') {
        setError(resolveApiErrorMessage(parsed))
      } else {
        notify.apiError(err, t('emailDialog.toasts.error'))
      }
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('emailDialog.title')}</DialogTitle>
          <DialogDescription>{t('emailDialog.description')}</DialogDescription>
        </DialogHeader>

        {/* noValidate: the browser's own email check ignores the portal language, ours is translated */}
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current-email">{t('emailDialog.fields.current')}</Label>
            <Input id="current-email" value={me?.email ?? ''} disabled />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-email">{t('emailDialog.fields.new')}</Label>
            <Input
              id="new-email"
              type="email"
              value={newEmail}
              onChange={(e) => {
                setNewEmail(e.target.value)
                setError(null)
              }}
              autoComplete="email"
              autoFocus
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t('buttons.cancel', { ns: 'common' })}
            </Button>
            <Button type="submit" disabled={!newEmail.trim() || isPending}>
              {isPending ? t('buttons.saving', { ns: 'common' }) : t('emailDialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
